import { CartItem } from '../types/CartItem';
// ⬇️ NEW: use productService instead of mockProducts
import { productService, normalizeBarcodeLike } from '../services/productService';

export interface ScannerState {
  barcode: string;
  error: string | null;
  foundProduct: CartItem | null;
  isProcessing: boolean;
  isAutoAdding: boolean;
  showSuccess: boolean;
  isAutoScanning: boolean;
  isSubmitting: boolean;
}

export interface ScannerCallbacks {
  onItemScanned: (item: CartItem) => void;
  onStateChange: (state: Partial<ScannerState>) => void;
}

export class ScannerManager {
  private callbacks: ScannerCallbacks;
  private scanBuffer: string = '';
  private scanTimeout: NodeJS.Timeout | null = null;
  private lastSubmissionTime: number = 0;

  constructor(callbacks: ScannerCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Process a barcode/UPC/SKU input and find the corresponding product (via API)
   */
  async processBarcode(input: string): Promise<CartItem | null> {
    console.log('🔍 SCANNING STARTED - Input:', input);

    const trimmed = (input ?? '').trim();
    if (!trimmed) {
      console.log('❌ Empty barcode input');
      this.callbacks.onStateChange({ error: 'Please enter a barcode' });
      return null;
    }

    this.callbacks.onStateChange({ isProcessing: true });

    try {
      const cleanBarcode = normalizeBarcodeLike(trimmed) ?? trimmed;
      console.log('Clean barcode:', cleanBarcode);

      const product = await productService.findByCode(cleanBarcode);

      console.log('Found product:', product);

      if (product && product.stockQuantity > 0) {
        const cartItem: CartItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          barcode: product.barcode,
          category: product.category,
          description: product.description,
          imageUrl: product.imageUrl,
          isbn: product.isbn,
          author: product.author,
          publisher: product.publisher,
          quantity: 1,
        };

        console.log('✅ Product found!');
        this.callbacks.onStateChange({
          foundProduct: cartItem,
          error: null,
          isProcessing: false
        });

        return cartItem;
      } else if( product && product.stockQuantity <= 0 ) {
        console.log('❌ Product out of stock:', product.name);
        this.callbacks.onStateChange({
          error: `Product ${product.name} is out of stock.`,
          isProcessing: false
        });
        return null;
      } else {
        console.log('❌ No product found for code:', input);
        this.callbacks.onStateChange({
          error: `Product with code ${input} not found in inventory.`,
          isProcessing: false
        });
        return null;
      }
    } catch (error) {
      console.error('Error processing barcode:', error);
      this.callbacks.onStateChange({
        error: 'Error processing barcode. Please try again.',
        isProcessing: false
      });
      return null;
    }
  }

  /** Auto-add product to cart after processing (unchanged) */
  async autoAddToCart(product: CartItem): Promise<void> {
    console.log('🛒 Auto-adding to cart:', product.name);

    this.callbacks.onStateChange({ isAutoAdding: true });

    setTimeout(() => {
      this.callbacks.onItemScanned(product);
      this.callbacks.onStateChange({
        foundProduct: null,
        barcode: '',
        isAutoAdding: false,
        showSuccess: true
      });

      console.log('✅ Product added to cart successfully');

      setTimeout(() => {
        this.callbacks.onStateChange({ showSuccess: false });
      }, 3000);
    }, 2000);
  }

  /** Handle manual barcode submission (unchanged) */
  async handleManualSubmit(barcode: string): Promise<void> {
    const product = await this.processBarcode(barcode);
    if (product) {
      await this.autoAddToCart(product);
    }
  }

  /** Handle auto-scan input (unchanged) */
  async handleAutoScan(input: string): Promise<void> {
    console.log('🔍 AUTO-SCAN DETECTED:', input);

    this.callbacks.onStateChange({
      isAutoScanning: true,
      barcode: input
    });

    const product = await this.processBarcode(input);
    if (product) {
      await this.autoAddToCart(product);
    }

    this.callbacks.onStateChange({ isAutoScanning: false });
  }

  /** Keyboard listener (unchanged) */
  setupGlobalScanListener(): () => void {
    const handleGlobalScan = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }

      const now = Date.now();
      if (now - this.lastSubmissionTime < 1000) {
        console.log('⚠️ DEBOUNCE: Ignoring rapid input');
        return;
      }

      if (this.scanTimeout) clearTimeout(this.scanTimeout);

      this.scanBuffer += event.key;

      if (this.scanBuffer.length > 50) this.scanBuffer = this.scanBuffer.slice(-20);

      if (event.key === 'Enter' && this.scanBuffer.length > 5) {
        const scannedBarcode = this.scanBuffer.slice(0, -1);
        this.lastSubmissionTime = now;
        this.handleAutoScan(scannedBarcode);
        this.scanBuffer = '';
        return;
      }

      if (this.scanBuffer.length > 10) {
        console.log('🔍 RAPID SCAN DETECTED:', this.scanBuffer);
        this.scanTimeout = setTimeout(() => {
          console.log('🔍 AUTO-SUBMITTING SCANNED BARCODE:', this.scanBuffer);
          this.lastSubmissionTime = now;
          this.handleAutoScan(this.scanBuffer);
          this.scanBuffer = '';
        }, 300);
      }
    };

    document.addEventListener('keydown', handleGlobalScan);

    return () => {
      document.removeEventListener('keydown', handleGlobalScan);
      if (this.scanTimeout) clearTimeout(this.scanTimeout);
    };
  }

  clearState(): void {
    this.callbacks.onStateChange({
      barcode: '',
      error: null,
      foundProduct: null,
      isProcessing: false,
      isAutoAdding: false,
      showSuccess: false,
      isAutoScanning: false,
      isSubmitting: false
    });
  }

  destroy(): void {
    if (this.scanTimeout) clearTimeout(this.scanTimeout);
    this.scanBuffer = '';
  }
}
